import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft, 
  Plus,
  Minus,
  Trash2,
  ShoppingBag,
  MapPin,
  AlertTriangle
} from "lucide-react";
import axios from "axios";
import { API } from "../utils/api";
import SuspensionBanner from "../components/SuspensionBanner";
import { repriceCartWithItems } from "../utils/pricing";

const CartPage = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryLocation, setDeliveryLocation] = useState(null);
  const [isSuspended, setIsSuspended] = useState(false);
  const [suspensionMessage, setSuspensionMessage] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("fleety_user");
    if (!storedUser) {
      navigate("/login");
      return;
    }
    const userData = JSON.parse(storedUser);
    setUser(userData);
    
    // Check user suspension status
    const checkUserStatus = async () => {
      try {
        const response = await axios.get(`${API}/user/status/${userData.id}`);
        if (response.data.is_suspended) {
          setIsSuspended(true);
          setSuspensionMessage(response.data.message);
        }
      } catch (error) {
        console.error("Error checking user status:", error);
        // Check from stored user data as fallback
        if (userData.is_suspended || userData.status === "suspended") {
          setIsSuspended(true);
          setSuspensionMessage("Your account has been suspended. You cannot place orders. Please contact Fleety support.");
        }
      }
    };
    
    checkUserStatus();
    
    // Get delivery address from localStorage (set by HomePage dropdown)
    const savedAddress = localStorage.getItem("fleety_delivery_address");
    if (savedAddress) {
      const address = JSON.parse(savedAddress);
      if (address.type === "gps") {
        setDeliveryAddress("📍 Current Location");
        setDeliveryLocation({ lat: address.lat, lng: address.lng });
      } else {
        setDeliveryAddress(`${address.label || "Home"} - ${address.address_line1}, ${address.address_line2 || ""}, ${address.pincode}`);
        setDeliveryLocation({ pincode: address.pincode });
      }
    } else {
      // Fall back to user profile address
      setDeliveryAddress(`${userData.label || "Home"} - ${userData.address_line1}, ${userData.address_line2 || ""}, ${userData.pincode}`);
      setDeliveryLocation({ pincode: userData.pincode });
    }
  }, [navigate]);

  useEffect(() => {
    if (user) {
      fetchCart();
    }
  }, [user, deliveryLocation]);

  const fetchCart = async () => {
    try {
      let params = {};
      
      // Get full address for accurate distance calculation
      const savedAddress = localStorage.getItem("fleety_delivery_address");
      if (savedAddress) {
        const address = JSON.parse(savedAddress);
        if (address.lat && address.lng) {
          // GPS coordinates
          params = { lat: address.lat, lng: address.lng };
        } else if (address.address_line1) {
          // Full address - send for geocoding
          const fullAddress = `${address.address_line1}, ${address.address_line2 || ''}, ${address.pincode}`;
          params = { address: fullAddress, pincode: address.pincode };
        } else if (address.pincode) {
          params = { pincode: address.pincode };
        }
      } else if (user?.pincode) {
        params = { pincode: user.pincode };
      }
      
      const response = await axios.get(`${API}/cart/${user.id}`, { params });
      setCart(response.data);
    } catch (error) {
      console.error("Error fetching cart:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return undefined;
    const refreshVisibleCart = () => {
      if (document.visibilityState === "visible") fetchCart();
    };
    window.addEventListener("focus", refreshVisibleCart);
    document.addEventListener("visibilitychange", refreshVisibleCart);
    return () => {
      window.removeEventListener("focus", refreshVisibleCart);
      document.removeEventListener("visibilitychange", refreshVisibleCart);
    };
  }, [user, deliveryLocation]);

  const updateQuantity = async (item, newQuantity) => {
    setSaving(true);
    const nextItems = (cart?.items || [])
      .filter((cartItem) => cartItem.item_id !== item.item_id || newQuantity > 0)
      .map((cartItem) =>
        cartItem.item_id === item.item_id
          ? { ...cartItem, quantity: newQuantity }
          : cartItem
      );
    setCart((current) => repriceCartWithItems(current, nextItems));
    try {
      await axios.post(`${API}/cart/update`, {
        user_id: user.id,
        item_id: item.item_id,
        restaurant_id: item.restaurant_id,
        name: item.name,
        price: item.price,
        quantity: newQuantity,
        image: item.image
      });
    } catch (error) {
      console.error("Error updating quantity:", error);
      await fetchCart();
    } finally {
      setSaving(false);
    }
  };

  const clearCart = async () => {
    if (window.confirm("Are you sure you want to clear your cart?")) {
      try {
        await axios.delete(`${API}/cart/${user.id}`);
        fetchCart();
      } catch (error) {
        console.error("Error clearing cart:", error);
      }
    }
  };

  const getDeliveryAddress = () => {
    return deliveryAddress;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3FA9F5] border-t-transparent"></div>
      </div>
    );
  }

  const isEmpty = !cart?.items?.length;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="cart-page">
      {/* Suspension Banner - shown when account is suspended */}
      {isSuspended && <SuspensionBanner message={suspensionMessage} />}
      
      {/* Top Spacer for mobile */}
      <div className="h-6 bg-white"></div>
      
      {/* Header */}
      <header className="bg-white px-4 py-4 shadow-sm sticky top-0 z-40">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100"
            data-testid="back-btn"
          >
            <ArrowLeft size={24} className="text-gray-700" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Your Cart</h1>
            {cart?.restaurant_name && (
              <p className="text-sm text-gray-500">{cart.restaurant_name}</p>
            )}
          </div>
          {!isEmpty && (
            <button 
              onClick={clearCart}
              disabled={saving}
              className="ml-auto text-red-500 hover:text-red-600"
              data-testid="clear-cart-btn"
            >
              <Trash2 size={20} />
            </button>
          )}
        </div>
      </header>

      {isEmpty ? (
        /* Empty Cart */
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
          <ShoppingBag size={64} className="text-gray-300 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Your cart is empty</h2>
          <p className="text-gray-500 mb-6 text-center">Add some delicious food to your cart!</p>
          <Button
            onClick={() => navigate("/home")}
            className="bg-[#3FA9F5] hover:bg-[#2196F3] text-white px-8"
            data-testid="browse-restaurants-btn"
          >
            Browse Restaurants
          </Button>
        </div>
      ) : (
        <div className="max-w-3xl mx-auto px-4 py-6">
          {/* Delivery Address */}
          <div className="bg-white rounded-xl p-4 shadow-sm mb-4" data-testid="delivery-address">
            <div className="flex items-start gap-3">
              <MapPin size={20} className="text-[#3FA9F5] mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">Delivering to</p>
                <p className="font-medium text-gray-900">{getDeliveryAddress()}</p>
              </div>
            </div>
          </div>

          {/* Cart Items */}
          <div className="bg-white rounded-xl shadow-sm mb-4" data-testid="cart-items">
            <div className="p-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">{cart.restaurant_name}</h2>
            </div>
            <div className="divide-y divide-gray-100">
              {cart.items.map((item) => (
                <div key={item.item_id} className="p-4 flex items-center gap-4" data-testid={`cart-item-${item.item_id}`}>
                  <img 
                    src={item.image} 
                    alt={item.name}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{item.name}</h3>
                    <p className="text-[#3FA9F5] font-semibold">₹{item.price}</p>
                  </div>
                  <div className="flex items-center gap-2 bg-gray-100 rounded-lg px-2 py-1">
                    <button 
                      onClick={() => updateQuantity(item, item.quantity - 1)}
                      disabled={saving}
                      className="text-[#3FA9F5]"
                      data-testid={`minus-${item.item_id}`}
                    >
                      <Minus size={16} />
                    </button>
                    <span className="font-semibold min-w-[20px] text-center">{item.quantity}</span>
                    <button 
                      onClick={() => updateQuantity(item, item.quantity + 1)}
                      disabled={saving}
                      className="text-[#3FA9F5]"
                      data-testid={`plus-${item.item_id}`}
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                  <p className="font-semibold text-gray-900 min-w-[60px] text-right">
                    ₹{item.price * item.quantity}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Bill Details */}
          <div className="bg-white rounded-xl shadow-sm p-4 mb-4" data-testid="bill-details">
            <h2 className="font-semibold text-gray-900 mb-4">Bill Details</h2>
            <div className="space-y-3 text-sm">
              {/* Food Section */}
              <div className="flex justify-between">
                <span className="text-gray-600">Item Total</span>
                <span className="font-medium">₹{cart.item_total?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Packing Charges</span>
                <span className="font-medium">₹{cart.packing_charges?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between border-t border-gray-100 pt-2">
                <span className="text-gray-700 font-medium">Subtotal (Food + Packing)</span>
                <span className="font-medium">₹{cart.subtotal?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">GST on Food & Packing (5%)</span>
                <span className="font-medium">₹{cart.food_gst?.toFixed(2)}</span>
              </div>
              
              {/* Delivery & Platform Section */}
              <div className="border-t border-gray-100 pt-2"></div>
              <div className="flex justify-between">
                <span className="text-gray-600">Delivery Fee ({cart.distance?.toFixed(1) || 0} km)</span>
                <span className="font-medium">₹{cart.delivery_fee?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Platform Fee</span>
                <span className="font-medium">₹{cart.platform_fee?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">GST on Delivery & Platform (18%)</span>
                <span className="font-medium">₹{cart.service_gst?.toFixed(2)}</span>
              </div>
              
              {/* Total */}
              <div className="border-t border-gray-200 pt-3 flex justify-between">
                <span className="font-semibold text-gray-900">To Pay</span>
                <span className="font-bold text-lg text-gray-900">₹{cart.total?.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Suspension Warning */}
          {isSuspended && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700">Your account is suspended. You cannot proceed to checkout.</p>
            </div>
          )}

          {/* Checkout Button */}
          <Button
            onClick={() => !isSuspended && navigate("/checkout")}
            disabled={isSuspended || saving}
            className={`w-full h-14 text-white text-lg font-semibold rounded-xl shadow-lg ${
              isSuspended 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-[#3FA9F5] hover:bg-[#2196F3]'
            }`}
            data-testid="checkout-btn"
          >
            {isSuspended ? "Account Suspended" : `Proceed to Checkout • ₹${cart.total}`}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CartPage;
